import {Component, OnInit, Input, Output, ViewChild, ElementRef, HostBinding} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Shell } from '@tytapp/common';
import { ApiOffer, ApiOfferSet, ApiUser } from '@tytapp/api';
import {ActivatedRoute, Params, Router} from '@angular/router';
import { UserService } from '@tytapp/user';
import { SubSink } from 'subsink';
import { Subject } from 'rxjs';
import { environment } from 'environments/environment';

@Component({
  selector: 'tyt-pricing-offers-step',
  templateUrl: './pricing-offers-step.component.html',
  styleUrls: ['./pricing-offers-step.component.scss']
})
export class PricingOffersStepComponent implements OnInit {

  constructor(
    private shell : Shell,
    private router : Router,
    private userService : UserService,
    private currencyPipe : CurrencyPipe,
    private activatedRoute: ActivatedRoute
  ) { }

  @Input()
  inSettings: boolean;

  private _offerSet : ApiOfferSet;

  @Input()
  get offerSet() : ApiOfferSet {
    return this._offerSet;
  }

  @Input()
  @HostBinding('class.previewing')
  previewing : boolean;

  @Input()
  showNoThanks: boolean = false;

  @Input()
  knownEmail: string;

  @Input()
  knownFirstName: string;

  @Output()
  periodChanged = new Subject<string>();

  public hideSponsoredOffersWhenUnavailable: boolean;

  get sendToSettings() {
    return false;
    // return this.user
    //   && this.user.membership
    //   && this.user.membership.active
    //   && this.user.membership.gateway_name !== 'free'
    //   && !this.inSettings;
  }

  get actBlueLink() {
    return this.offerSet?.actblue_url;
  }

  isHtmlEmpty(html : string) {
    let text = html.replace(/<[^>]+>/g, '').replace(/^ */g, '').replace(/ *$/, '');
    if (text === '') {
      if (!['<img', '<iframe'].some(snip => html.includes(snip)))
        return true;
    }
    return false;
  }

  set offerSet(value) {
    this._offerSet = value;
    this.updatePeriodLabelsAndAvailablePeriods(); // Update labels and available periods.
    this.isEmbedded = this._offerSet.kind === 'embedded';
    this.routeParamsUpdated(this.activatedRoute.snapshot.queryParams);
  }
  
  oneTimeNoticeVisible = true;

  submitOtherAmount() {
    if (this.otherAmount < this.minCustomPrice || this.otherAmount > this.maxCustomPrice)
      return;

    let step = 'signup';
    if (this.user)
      step = 'payment';

    if(this.inSettings) {
      this.router.navigateByUrl(`/settings/one-time-contribution/payment?offerId=x&price=${this.otherAmount*100}`);
    } else {
      let url = `/join/m/${this.offerSet.uuid}/p:${this.otherAmount*100}:${this.selectedPeriod}/${step}`;
      const params = this.getKnownValuesQueryParams();
      let queryParams = '';

      for(let key of Object.keys(params)) {
        queryParams = queryParams + `${key}=${params[key]}&`;
      }

      if(queryParams) {
        url = url + '?' + queryParams;
      }

      window.parent.location.href = url;
    }
  }

  getKnownValuesQueryParams() {
    let params = {};

    if(this.knownEmail) {
      params['email'] = this.knownEmail;
    }
    if(this.knownFirstName) {
      params['firstName'] = this.knownFirstName;
    }

    return params;
  }

  oneTimeMessageType: string;

  get minimumOneTimeEntitlement(): number {
    if(this.offerSet.entitlement_thresholds && this.offerSet.entitlement_thresholds.length > 0) {
      var minimumAmount = this.offerSet.entitlement_thresholds.reduce((previous, current) => {
        if(previous.amount < current.amount )
          return previous;
        else
          return current;
      });
      return minimumAmount.amount / 100;
    } else {
      return null;
    }

  }
  oneTimeCustomMessage: string;

  getOneTimeCustomMessage(): string {
    if(this.selectedPeriod === 'one_time') {
      if(this.otherAmount < this.minCustomPrice) {
        this.oneTimeMessageType = 'below-min';
        return `
          To cover our processing costs, we can’t accept contributions less than
          ${ this.currencyPipe.transform(this.minCustomPrice, 'USD')}${this.frequencySuffixForPeriod(this.selectedPeriod)}
        `;
      }
      if(this.minimumOneTimeEntitlement != null && this.otherAmount < this.minimumOneTimeEntitlement) {
        this.oneTimeMessageType = 'below-entitlement';
        return `Hey! If you contribute ${ this.currencyPipe.transform(this.minimumOneTimeEntitlement, 'USD') } or more,
          you'll unlock the full TYT Plus membership experience.`
      }
      if(this.minimumOneTimeEntitlement != null && this.otherAmount >= this.minimumOneTimeEntitlement) {
        this.oneTimeMessageType = 'entitled';
        return `Nice! Your generous contribution has granted you full access to TYT Plus Membership!`;
      }
    }
    this.oneTimeMessageType = '';
    return '';
  }

  private _selectedPeriod : string = 'monthly';

  joinButtonText = 'JOIN';

  set selectedPeriod(value : string) {
    if (value === this._selectedPeriod) return;

    this._selectedPeriod = value;
    this.otherAmount = null;

    if(value === 'one_time')
      this.joinButtonText = 'Donate';
    else
      this.joinButtonText = 'Join';
 
    this.periodChanged.next(value);
  }

  get selectedPeriod() : string {
    return this._selectedPeriod;
  }

  hasBelowTheFold = false;
  showMoreOffers = false;
  availablePeriods : string[][] = [];
  periodLabels : Record<string,string> = {
    one_time: 'One Time',
    monthly: 'Monthly',
    annually: 'Annually'
  };

  _otherAmount: number;

  get otherAmount(): number {
    return this._otherAmount;
  }

  set otherAmount(value) {
    this._otherAmount = value;
    this.oneTimeCustomMessage = this.getOneTimeCustomMessage();
  }

  @ViewChild('otherAmountInput')
  otherAmountInput : ElementRef;

  get minCustomPrice(): number {
    if (this.offerSet && this.offerSet.is_pay_what_you_want) {
      if(this.selectedPeriod === 'monthly' && this.offerSet.minimum_monthly_price != null)
        return this.offerSet.minimum_monthly_price / 100;
      if(this.selectedPeriod === 'annually' && this.offerSet.minimum_annual_price != null)
        return this.offerSet.minimum_annual_price / 100;
      if(this.selectedPeriod === 'one_time' && this.offerSet.minimum_contribution_amount)
        return this.offerSet.minimum_contribution_amount / 100;
    }

    return 0.01;
  }

  get maxCustomPrice(): number {
    if (this.offerSet && this.offerSet.is_pay_what_you_want) {
      if (this.selectedPeriod === 'monthly' && this.offerSet.pwyw_monthly_introductory_periods > 0 && this.offerSet.pwyw_monthly_introductory_product) {
        return this.offerSet.pwyw_monthly_introductory_product.price / 100;
      } else if (this.selectedPeriod === 'annually' && this.offerSet.pwyw_annual_introductory_periods > 0 && this.offerSet.pwyw_annual_introductory_product) {
        return this.offerSet.pwyw_annual_introductory_product.price / 100;
      }
    }
    return 999999;
  }

  get isCustomPriceValid() {
    let stringAmount : string;

    // This works around the issue of prices like "3.90" which get converted
    // to a number and lose the zero on the end. We need to validate the raw input
    // from the user to ensure it is a valid USD amount

    if (this.otherAmountInput)
      stringAmount = this.otherAmountInput.nativeElement.value;
    else if(this.otherAmount != null)
      stringAmount = this.otherAmount.toString();

    console.log(`Checking '${stringAmount}'`);
    return /^\d+(\.\d\d)?$/.test(stringAmount);
  }

  get offersBySelectedPeriod() {
    if (!this.offerSet)
      return [];

    return this.offerSet.offers.filter(x => x.period === this.selectedPeriod);
  }

  subsink = new SubSink();
  user : ApiUser = null;
  nextStep = 'signup';

  isEmbedded: boolean = false;

  routeParamsUpdated({ period }: Params) {
    if (!this.offerSet)
      return;

    if (Object.keys(this.periodLabels).includes(period)) {
      this.selectedPeriod = period;
    } else {
      if (this.offerSet.default_period && this.offerSet.default_period !== 'auto') {
        this.selectedPeriod = this.offerSet.default_period;
      } else if (this.availablePeriods.length > 0) {
        this.selectedPeriod = this.availablePeriods[0][0];
      } else {
        this.selectedPeriod = 'monthly';
      }
    }
  }

  async ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => this.routeParamsUpdated(params));

    this.subsink.add(this.userService.userChanged.subscribe(user => {
      this.user = user;
      this.nextStep = user ? 'payment' : 'signup';
    }));
    this.shell.title = this.offerSet?.page_title ? this.offerSet.page_title : 'Become a Member';
  
    for (let offer of this.offerSet?.offers || []) {
      if ((<any>offer).above_the_fold === false) {
        this.hasBelowTheFold = true;
        break;
      }
    }

    this.hideSponsoredOffersWhenUnavailable = await this.shell.hasFeature('apps.accounts.hide_sponsored_offers_when_unavailable');
  
    if (typeof window !== 'undefined') {
      this.userService.sendBeacons('View Offer Set', () => {
        this.userService.sendBeaconGA4('view_item_list', {
          item_list_id: this.offerSet?.uuid,
          item_list_name: this.offerSet?.name,
          currency: 'USD',
          items: this.offerSet?.offers.map(offer => ({
            item_id: offer.membership_product?.product_sku,
            item_name: offer.membership_product?.name,
            item_brand: 'TYT',
            item_category: 'Membership',
            price: offer.dollar_price,
            quantity: 1
          }))
        });
      });
    }
  
    this.updatePeriodLabelsAndAvailablePeriods();
  }

  offerSelected(offer: ApiOffer) {
    this.userService.sendBeacons('Clicked Offer Set', () => {
      this.userService.sendBeaconGA4('add_to_cart', {
        currency: 'USD',
        value: offer.dollar_price,
        items: [{
          item_id: offer.membership_product?.product_sku,
          item_name: offer.membership_product?.name,
          item_brand: 'TYT',
          item_category: 'Membership',
          price: offer.dollar_price,
          item_list_id: this.offerSet.uuid,
          item_list_name: this.offerSet.name,
          quantity: 1
        }]
      });
    });
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  toggleMoreOffers() {
    this.showMoreOffers = !this.showMoreOffers;
  }

  frequencySuffixForPeriod(period : string) {
    if (period === 'monthly')
      return '/mo';
    else if (period === 'annually')
      return '/yr';
    return '';
  }

  frequencySuffixForOffer(offer : ApiOffer) {
    return this.frequencySuffixForPeriod(offer.period);
  }

  showOther = false;

  getOfferRouterLink(offer : ApiOffer): string {
    if (offer.subsidized && !offer.sponsorship_available)
      return null;

    if(this.inSettings) {
      return `/settings/one-time-contribution/payment`;
    } else {
      return `/join/m/${this.offerSet.uuid}/${offer.uuid}/${this.nextStep}`;
    }
  }

  get firstMembershipValueProp() {
    if (!this.offerSet || !this.offerSet.membership_value_props)
      return undefined;

    return this.offerSet.membership_value_props[0];
  }

  get moreMembershipValueProps() {
    if (!this.offerSet || !this.offerSet.membership_value_props)
      return [];

    return this.offerSet.membership_value_props.slice(1);
  }

  getOfferQueryParams(offer) {
    if(this.inSettings) {
      return {
        offerId: offer.uuid,
        price: offer.price
      };
    }
    else {
      return this.getKnownValuesQueryParams();
    }
  }

  get subsidizedPrice() {
    let defaultPrice = 1.99;

    if (!this.offerSet)
      return defaultPrice;
    let subsidizedOffer = this.offerSet.offers
      .filter(x => x.subsidized)
      .sort((a, b) => a.price - b.price)
      [0]
    ;

    if (!subsidizedOffer)
      return defaultPrice;

    return subsidizedOffer.price || subsidizedOffer.membership_product?.price;
  }

  get hasTier1Offers() {
    return this.offersBySelectedPeriod.some(x => x.above_the_fold);
  }

  get firstTier2Offer() {
    if (!this.offerSet)
      return null;

    return this.offersBySelectedPeriod.find(x => !x.above_the_fold);
  }

  get allowCustomPricing() {
    if (this.offerSet && (this.offerSet.uuid === '9f8496d' || this.offerSet.uuid.startsWith('z-')))
      return false;
    return true;
  }

  noThanksClicked() {
    if(typeof window !== 'undefined') {
      window.parent.postMessage({ event: 'noThanks' }, environment.urls.app);
    }
  }

  offerTierData(tier: 1 | 2, prop: 'label' | 'summary' | 'tooltip') {
    return this.offerSet[
      `${this.selectedPeriod === 'one_time' ? 'contribution_' : ''}tier${tier}_${prop}`
    ];
  }

  selectPeriod([period]: [string, string]) {
    this.selectedPeriod = period;
  }

  private updatePeriodLabelsAndAvailablePeriods() {
    if (this.offerSet) {
      this.periodLabels.one_time = this.offerSet.one_time_period_label || 'One Time';
      this.periodLabels.annually = this.offerSet.annually_period_label || 'Annually';
      this.periodLabels.monthly = this.offerSet.monthly_period_label || 'Monthly';
  
      this.availablePeriods =
        this.offerSet.offers
          .map(x => x.period)
          .reduce((pv, cv) => pv.includes(cv) ? pv : pv.concat([cv]), [])
          .map(x => [x, this.periodLabels[x]]);
    }
  }
  
}
